import { STOREFRONTS, localizationLanguagesForStorefront } from '../data/storefronts.js';

export const LOCALIZATION_GAP_REASON = 'missing-storefront-language';

/**
 * @typedef {object} LocalizationGap
 * @property {typeof LOCALIZATION_GAP_REASON} reason
 * @property {string} missingLanguage
 * @property {string[]} missingLanguages
 * @property {string[]} appLanguages
 */

/**
 * Detect when Apple reports an app as available in a storefront but its
 * languageCodesISO2A list does not include that storefront's local language.
 *
 * This is intentionally conservative: unknown, empty, or malformed app language
 * lists return null instead of guessing.
 *
 * @param {import('./normalizeApp.js').RegionRow} row
 * @param {Array<{code: string, primaryLang?: string, secondaryLangs?: string[]}>} [storefronts]
 * @returns {LocalizationGap|null}
 */
export function detectLocalizationGap(row, storefronts = STOREFRONTS) {
  if (!row || row.available === false || row.error) return null;

  const appLanguages = normalizeLanguageList(row.languageCodesISO2A);
  if (appLanguages.length === 0) return null;

  const storefront = findStorefront(row.countryCode, storefronts);
  const localLanguages = localizationLanguagesForStorefront(storefront);
  if (localLanguages.length === 0) return null;

  const appSet = new Set(appLanguages);
  const hasAnyLocalLanguage = localLanguages.some((lang) => appSet.has(lang));
  if (hasAnyLocalLanguage) return null;

  return {
    reason: LOCALIZATION_GAP_REASON,
    missingLanguage: localLanguages[0],
    missingLanguages: localLanguages,
    appLanguages,
  };
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeLanguageList(value) {
  if (!Array.isArray(value) || value.length === 0) return [];
  const normalized = value
    .map(baseLanguageCode)
    .filter(Boolean);
  return [...new Set(normalized)];
}

function findStorefront(countryCode, storefronts) {
  const code = String(countryCode ?? '').trim().toLowerCase();
  return (storefronts ?? []).find((sf) => String(sf?.code ?? '').toLowerCase() === code) ?? null;
}

function baseLanguageCode(value) {
  if (typeof value !== 'string') return null;
  const match = value.trim().toLowerCase().match(/^[a-z]{2}/);
  return match ? match[0] : null;
}
